import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { userPermissionsAtom, userRolesAtom } from '@/atoms/auth';
import { useReturningUser } from '@/hooks/analytics/useReturningUser';
import { useOverduePayment } from '@/hooks/billing/useOverduePayment';
import { useSettings } from '@/hooks/state/useSettings';

import type { GatewaySnapshot } from '../types';
import type { FileFormat } from '@/types/file-format';
import type { VideoResolution } from '@/types/video-resolution';

/**
 * Composes a `GatewaySnapshot` from existing hooks and atoms. The
 * snapshot is plain data — passed verbatim into `evaluateDownload`.
 *
 * Internal to the gateway. The fixture builder used in tests lives at
 * `Gateway/test-fixtures.ts` and produces the same shape.
 */
export const useGatewaySnapshot = (): GatewaySnapshot => {
  const { settings, isLoading } = useSettings();
  const { overduePayment } = useOverduePayment();
  const {
    data: { isReturningUser },
  } = useReturningUser();
  const permissions = useAtomValue(userPermissionsAtom);
  const roles = useAtomValue(userRolesAtom);

  return useMemo<GatewaySnapshot>(
    () => ({
      isSettingsLoaded: !isLoading && !!settings,
      user: {
        isAuthenticated: !!settings?.user?.is_authenticated,
        isEmailVerified: !!settings?.user?.isEmailVerified,
        isReturningUser: !!isReturningUser,
      },
      permissions,
      roles,
      credits: settings?.credits?.credits_current ?? 0,
      creditUsageAcknowledged:
        !!settings?.preferences?.credit_usage_acknowledged,
      overduePayment: !!overduePayment,
      subscriptionPaused: !!settings?.subscriptionData?.isPaused,
      formatPreference: settings?.preferences?.file_format as
        | FileFormat
        | undefined,
      resolutionPreference: settings?.preferences?.video_file_format as
        | VideoResolution
        | undefined,
    }),
    // Listing the leaf settings fields only — including `settings` itself
    // would defeat the point of the granular deps (any referential change to
    // the settings object would recompute even when none of the fields the
    // snapshot reads have changed).
    [
      isLoading,
      settings?.user?.is_authenticated,
      settings?.user?.isEmailVerified,
      settings?.credits?.credits_current,
      settings?.subscriptionData?.isPaused,
      settings?.preferences?.credit_usage_acknowledged,
      settings?.preferences?.file_format,
      settings?.preferences?.video_file_format,
      isReturningUser,
      permissions,
      roles,
      overduePayment,
    ]
  );
};
